import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, Flame, Wifi, Plus, LogOut, ArrowRight, Trash2, Upload, Loader2, Calendar, Euro,
  AlertTriangle, CheckCircle2, FileText, Edit2, Sparkles, TrendingDown, Shield, Clock,
  BadgeCheck, Save, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { TOKEN_KEY, USER_KEY } from './PortalAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api/portal` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      window.location.href = '/clienti/login';
    }
    return Promise.reject(err);
  }
);

const SERV_META = {
  luce: { Icon: Zap, label: 'Luce', color: 'from-amber-400 to-orange-500', ring: 'from-amber-400/40 to-orange-500/10', text: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-400/30' },
  gas: { Icon: Flame, label: 'Gas', color: 'from-sky-400 to-cyan-500', ring: 'from-sky-400/40 to-cyan-500/10', text: 'text-sky-300', bg: 'bg-sky-500/10', border: 'border-sky-400/30' },
  fibra: { Icon: Wifi, label: 'Fibra', color: 'from-fuchsia-400 to-rose-500', ring: 'from-fuchsia-400/40 to-rose-500/10', text: 'text-fuchsia-300', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-400/30' },
};

const Shell = ({ children, user, title }) => {
  const navigate = useNavigate();
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    navigate('/clienti/login', { replace: true });
  };
  return (
    <div className="min-h-[100dvh] bg-[#0a1628] text-slate-100 antialiased relative">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#0a0f1c] pointer-events-none" />
      <div className="fixed top-1/4 -left-20 w-96 h-96 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none" />
      <header className="relative z-10 sticky top-0 backdrop-blur bg-[#0a1628]/80 border-b border-white/5 safe-top">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-sky-500/20 border border-sky-500/30 shrink-0">
              <Zap className="w-5 h-5 text-sky-400" />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-bold text-sm sm:text-base truncate">{title || 'Area Clienti'}</div>
              <div className="text-[10px] sm:text-xs text-sky-300/70 -mt-0.5 truncate">
                {user ? `Ciao, ${user.nome}` : 'F.lli Schilirò Servizi'}
              </div>
            </div>
          </div>
          <button onClick={logout} data-testid="portal-logout"
            className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-white/10 hover:border-rose-400/40 hover:bg-rose-500/5 transition-colors inline-flex items-center gap-1.5">
            <LogOut className="w-3.5 h-3.5" /> Esci
          </button>
        </div>
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
    </div>
  );
};

// ========== DASHBOARD ==========
export const PortalDashboard = () => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [utenze, setUtenze] = useState([]);
  const [proposte, setProposte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingUtenza, setEditingUtenza] = useState(null);
  const [requestingId, setRequestingId] = useState(null);
  const [respondingId, setRespondingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, utRes, prRes] = await Promise.all([
        api.get('/me'), api.get('/utenze'), api.get('/proposte'),
      ]);
      setUser(meRes.data);
      localStorage.setItem(USER_KEY, JSON.stringify(meRes.data));
      setUtenze(utRes.data || []);
      setProposte(prRes.data || []);
    } catch { /* interceptor handles */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const respondProposta = async (prop, action) => {
    setRespondingId(prop.id);
    try {
      await api.post(`/proposte/${prop.id}/${action}`);
      toast.success(action === 'accetta' ? 'Grazie! La conferma è arrivata all\'agenzia.' : 'Risposta registrata');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setRespondingId(null); }
  };

  const removeUtenza = async (id) => {
    if (!window.confirm('Eliminare questa utenza?')) return;
    try { await api.delete(`/utenze/${id}`); setUtenze((p) => p.filter((u) => u.id !== id)); toast.success('Utenza eliminata'); }
    catch { toast.error('Errore'); }
  };

  const requestPreventivo = async (utenza) => {
    setRequestingId(utenza.id);
    try {
      await api.post(`/utenze/${utenza.id}/richiedi-preventivo`);
      toast.success('Richiesta inviata! Un consulente ti contatterà a breve.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio richiesta');
    } finally { setRequestingId(null); }
  };

  const stats = useMemo(() => {
    const byServ = { luce: 0, gas: 0, fibra: 0 };
    let totMensile = 0;
    let inScadenza = 0;
    utenze.forEach((u) => {
      byServ[u.servizio] = (byServ[u.servizio] || 0) + 1;
      if (u.ultima_bolletta_importo) totMensile += u.ultima_bolletta_importo;
      if (u.in_scadenza) inScadenza += 1;
    });
    return { byServ, totMensile, inScadenza };
  }, [utenze]);

  return (
    <Shell user={user} title="La tua Area Clienti">
      {loading ? (
        <div className="h-64 grid place-items-center">
          <Loader2 className="w-6 h-6 animate-spin text-sky-300" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* KPI */}
          <div className="grid sm:grid-cols-3 gap-3">
            <Kpi label="Totale ultime bollette" value={stats.totMensile ? `${stats.totMensile.toFixed(2)} €` : '—'} Icon={Euro} iconColor="text-sky-400" iconBg="bg-sky-500/15 border-sky-500/30" />
            <Kpi label="Utenze gestite" value={utenze.length} Icon={BadgeCheck} iconColor="text-cyan-400" iconBg="bg-cyan-500/15 border-cyan-500/30" />
            <Kpi label="Offerte in scadenza" value={stats.inScadenza} Icon={AlertTriangle} iconColor={stats.inScadenza > 0 ? 'text-rose-400' : 'text-slate-500'} iconBg={stats.inScadenza > 0 ? 'bg-rose-500/15 border-rose-500/30' : 'bg-white/5 border-white/10'} highlight={stats.inScadenza > 0} />
          </div>

          {/* Offerte per te */}
          {proposte.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-cyan-300" /> Offerte per te
                </h2>
                <span className="text-xs px-2.5 py-1 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 font-semibold">
                  {proposte.filter(p => p.stato === 'inviata' || p.stato === 'visualizzata').length} da valutare
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {proposte.map((p) => (
                  <ProposalCard key={p.id} proposta={p} loading={respondingId === p.id}
                    onAccept={() => respondProposta(p, 'accetta')}
                    onReject={() => respondProposta(p, 'rifiuta')} />
                ))}
              </div>
            </div>
          )}

          {/* Utenze */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl sm:text-2xl font-bold">Le mie utenze</h2>
              <Button data-testid="btn-add-utenza" onClick={() => setShowAdd(true)}
                className="h-9 bg-sky-500 hover:bg-sky-400 text-white font-semibold">
                <Plus className="w-4 h-4 mr-1.5" /> Aggiungi utenza
              </Button>
            </div>

            {utenze.length === 0 ? (
              <Card className="bg-slate-900/70 border-dashed border-white/10">
                <CardContent className="p-10 text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-sky-500/15 border border-sky-500/30 grid place-items-center">
                    <Upload className="w-6 h-6 text-sky-400" />
                  </div>
                  <div className="mt-4 font-semibold">Nessuna utenza caricata</div>
                  <div className="text-sm text-slate-400 mt-1 mb-5">
                    Carica una bolletta (luce, gas o fibra) e ti aiuteremo a monitorare la scadenza dell'offerta.
                  </div>
                  <Button data-testid="btn-first-utenza" onClick={() => setShowAdd(true)}
                    className="bg-sky-500 hover:bg-sky-400 text-white font-semibold">
                    <Plus className="w-4 h-4 mr-1.5" /> Carica la prima bolletta
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {utenze.map((u) => (
                  <UtenzaCard
                    key={u.id} utenza={u}
                    onEdit={() => setEditingUtenza(u)}
                    onDelete={() => removeUtenza(u.id)}
                    onRequest={() => requestPreventivo(u)}
                    requesting={requestingId === u.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <UploadUtenzaDialog
        open={showAdd} onClose={() => setShowAdd(false)}
        onSaved={() => { setShowAdd(false); load(); toast.success('Utenza aggiunta! Dati estratti dalla bolletta.'); }}
      />
      <EditUtenzaDialog
        utenza={editingUtenza} onClose={() => setEditingUtenza(null)}
        onSaved={() => { setEditingUtenza(null); load(); toast.success('Dati aggiornati'); }}
      />
    </Shell>
  );
};

const Kpi = ({ label, value, Icon, iconColor, iconBg, highlight }) => (
  <Card className={`bg-slate-900/70 text-slate-100 ${highlight ? 'border-rose-400/30' : 'border-white/10'}`}>
    <CardContent className="p-4 sm:p-5 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl border grid place-items-center shrink-0 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 truncate">{label}</div>
        <div className="font-bold text-lg sm:text-xl truncate">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const UtenzaCard = ({ utenza, onEdit, onDelete, onRequest, requesting }) => {
  const meta = SERV_META[utenza.servizio] || SERV_META.luce;
  const { Icon } = meta;
  const scaduta = utenza.giorni_a_scadenza !== null && utenza.giorni_a_scadenza < 0;
  return (
    <Card data-testid={`utenza-card-${utenza.id}`}
      className={`relative bg-slate-900/70 text-slate-100 border transition-all ${
        utenza.in_scadenza ? 'border-rose-400/40 shadow-lg shadow-rose-500/5' : scaduta ? 'border-amber-400/40' : 'border-white/10'
      }`}
    >
      {utenza.in_scadenza && (
        <div data-testid={`badge-in-scadenza-${utenza.id}`}
          className="absolute -top-2.5 right-3 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-rose-500 text-white uppercase tracking-wider inline-flex items-center gap-1 z-10">
          <AlertTriangle className="w-3 h-3" /> In scadenza · {utenza.giorni_a_scadenza}gg
        </div>
      )}
      {scaduta && !utenza.in_scadenza && (
        <div className="absolute -top-2.5 right-3 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-amber-500 text-slate-900 uppercase tracking-wider inline-flex items-center gap-1 z-10">
          Scaduta
        </div>
      )}
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.color} grid place-items-center shrink-0`}>
            <Icon className="w-5 h-5 text-slate-950" />
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onEdit} data-testid={`edit-${utenza.id}`} className="w-8 h-8 rounded-lg hover:bg-white/10 grid place-items-center text-slate-400 hover:text-slate-100">
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={onDelete} data-testid={`delete-${utenza.id}`} className="w-8 h-8 rounded-lg hover:bg-rose-500/10 grid place-items-center text-slate-400 hover:text-rose-300">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{meta.label}{utenza.etichetta ? ` · ${utenza.etichetta}` : ''}</div>
          <div className="mt-0.5 font-bold text-base truncate">{utenza.fornitore || <span className="text-slate-500">Fornitore sconosciuto</span>}</div>
          <div className="text-sm text-slate-400 truncate">{utenza.offerta_nome || <span className="text-slate-600">Offerta non rilevata</span>}</div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <KV label="Scadenza offerta" value={utenza.scadenza_offerta || '—'} highlight={utenza.in_scadenza} />
          <KV label="Ultima bolletta" value={utenza.ultima_bolletta_importo ? `${Number(utenza.ultima_bolletta_importo).toFixed(2)} €` : '—'} testId={`ultima-bolletta-${utenza.id}`} />
          {utenza.servizio !== 'fibra' && <KV label={utenza.servizio === 'gas' ? 'Prezzo €/Smc' : 'Prezzo €/kWh'} value={utenza.costo_unitario ? `${utenza.costo_unitario}` : '—'} compact testId={`prezzo-${utenza.id}`} />}
          <KV label={utenza.servizio === 'fibra' ? 'Canone / mese' : 'Quota fissa / mese'} value={utenza.quota_fissa_mensile ? `${Number(utenza.quota_fissa_mensile).toFixed(2)} €` : '—'} compact testId={`quota-fissa-${utenza.id}`} />
          {utenza.servizio === 'luce' && <KV label="POD" value={utenza.pod || '—'} compact />}
          {utenza.servizio === 'gas' && <KV label="PDR" value={utenza.pdr || '—'} compact />}
        </div>
        {utenza.ultima_bolletta_importo && (
          <div data-testid={`riepilogo-${utenza.id}`} className="mt-3 text-[11px] text-slate-400 leading-relaxed">
            L'ultima bolletta{utenza.periodo_fatturazione ? ` (${utenza.periodo_fatturazione})` : ''} è stata di {Number(utenza.ultima_bolletta_importo).toFixed(2)} €
            {utenza.costo_unitario ? `, prezzo ${utenza.costo_unitario} ${utenza.servizio === 'gas' ? '€/Smc' : '€/kWh'}` : ''}
            {utenza.quota_fissa_mensile ? `, costo fisso mensile ${Number(utenza.quota_fissa_mensile).toFixed(2)} €` : ''}.
          </div>
        )}
        <Button data-testid={`request-${utenza.id}`} onClick={onRequest} disabled={requesting}
          className="w-full mt-5 h-10 bg-sky-500 hover:bg-sky-400 text-white font-semibold text-sm">
          {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><TrendingDown className="w-4 h-4 mr-1.5" /> Richiedi un nuovo preventivo</>)}
        </Button>
        {!utenza.ocr_fatto && (
          <div className="mt-3 text-[11px] text-amber-300/80 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> Non siamo riusciti a leggere la bolletta: modifica i dati manualmente.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const KV = ({ label, value, highlight, compact, testId }) => (
  <div data-testid={testId} className={`rounded-lg bg-white/5 border border-white/10 ${compact ? 'px-2 py-1.5' : 'px-2.5 py-2'}`}>
    <div className="text-[9px] uppercase tracking-wider text-slate-500 truncate">{label}</div>
    <div className={`text-xs font-semibold truncate ${highlight ? 'text-rose-300' : 'text-slate-100'}`}>{value}</div>
  </div>
);

// --------- Upload Utenza ---------
const UploadUtenzaDialog = ({ open, onClose, onSaved }) => {
  const [servizio, setServizio] = useState('luce');
  const [etichetta, setEtichetta] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setServizio('luce'); setEtichetta(''); setFile(null); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Carica la bolletta'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('servizio', servizio);
      if (etichetta) fd.append('etichetta', etichetta);
      await api.post('/utenze', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-slate-900 border-white/10 text-slate-100 max-w-md" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>Aggiungi una utenza</DialogTitle>
          <DialogDescription className="text-slate-400">
            Carica la bolletta: estraiamo noi fornitore, nome offerta, scadenza e costi.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Tipo di utenza</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(SERV_META).map(([k, m]) => (
                <button key={k} type="button" data-testid={`choose-${k}`}
                  onClick={() => setServizio(k)}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border font-medium text-sm transition-colors ${
                    servizio === k ? 'bg-sky-500/15 border-sky-400/60 text-white' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                  }`}>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${m.color} grid place-items-center`}>
                    <m.Icon className="w-4 h-4 text-slate-950" />
                  </div>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Etichetta (opzionale)</Label>
            <Input value={etichetta} onChange={(e) => setEtichetta(e.target.value)} data-testid="etichetta-input"
              className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500"
              placeholder="Es. Casa Milano, Ufficio..." />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Allegato bolletta *</Label>
            <label htmlFor="utenza-file" data-testid="utenza-dropzone"
              className="mt-1 flex flex-col items-center justify-center gap-2 py-7 rounded-xl border-2 border-dashed border-white/15 bg-white/5 hover:bg-white/10 cursor-pointer">
              <Upload className="w-5 h-5 text-sky-400" />
              <span className="text-sm text-slate-300">{file ? file.name : 'Trascina o scegli il file (PDF, JPG, PNG, HEIC)'}</span>
              <span className="text-[11px] text-slate-500">L'AI leggerà i dati automaticamente</span>
              <input id="utenza-file" type="file" className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            {file && (
              <button type="button" onClick={() => setFile(null)} className="mt-1.5 text-xs text-slate-400 hover:text-rose-300">
                Rimuovi allegato
              </button>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/10 bg-transparent hover:bg-white/5 text-slate-200">
              Annulla
            </Button>
            <Button type="submit" disabled={loading} data-testid="utenza-submit"
              className="bg-sky-500 hover:bg-sky-400 text-white font-semibold">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analisi AI...</>) : (<>Carica e analizza<ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --------- Edit Utenza ---------
const EditUtenzaDialog = ({ utenza, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (utenza) setForm({ ...utenza }); }, [utenza]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        fornitore: form.fornitore || null,
        offerta_nome: form.offerta_nome || null,
        scadenza_offerta: form.scadenza_offerta || null,
        pod: form.pod || null,
        pdr: form.pdr || null,
        ultima_bolletta_importo: form.ultima_bolletta_importo ? Number(form.ultima_bolletta_importo) : null,
        periodo_fatturazione: form.periodo_fatturazione || null,
        costo_unitario: form.costo_unitario ? Number(form.costo_unitario) : null,
        quota_fissa_mensile: form.quota_fissa_mensile ? Number(form.quota_fissa_mensile) : null,
        etichetta: form.etichetta || null,
      };
      await api.put(`/utenze/${utenza.id}`, payload);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally { setLoading(false); }
  };

  if (!utenza) return null;
  return (
    <Dialog open={!!utenza} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-slate-900 border-white/10 text-slate-100 max-w-lg" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifica utenza</DialogTitle>
          <DialogDescription className="text-slate-400">
            Correggi i dati estratti dalla bolletta se qualcosa non è esatto.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-sm">Fornitore</Label>
              <Input value={form.fornitore || ''} onChange={(e) => setForm({ ...form, fornitore: e.target.value })} data-testid="edit-fornitore"
                className="mt-1 bg-slate-950/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Nome offerta</Label>
              <Input value={form.offerta_nome || ''} onChange={(e) => setForm({ ...form, offerta_nome: e.target.value })} data-testid="edit-offerta"
                className="mt-1 bg-slate-950/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Scadenza offerta</Label>
              <Input type="date" value={form.scadenza_offerta || ''} onChange={(e) => setForm({ ...form, scadenza_offerta: e.target.value })} data-testid="edit-scadenza"
                className="mt-1 bg-slate-950/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Importo ultima bolletta (€)</Label>
              <Input type="number" step="0.01" value={form.ultima_bolletta_importo || ''} onChange={(e) => setForm({ ...form, ultima_bolletta_importo: e.target.value })} data-testid="edit-importo"
                className="mt-1 bg-slate-950/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Periodo fatturato</Label>
              <Input value={form.periodo_fatturazione || ''} onChange={(e) => setForm({ ...form, periodo_fatturazione: e.target.value })} data-testid="edit-periodo"
                className="mt-1 bg-slate-950/50 border-white/10 text-white" placeholder="Es. Gen-Feb 2026" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">{utenza.servizio === 'fibra' ? 'Canone mensile (€)' : 'Quota fissa mensile (€)'}</Label>
              <Input type="number" step="0.01" value={form.quota_fissa_mensile || ''} onChange={(e) => setForm({ ...form, quota_fissa_mensile: e.target.value })} data-testid="edit-quota-fissa"
                className="mt-1 bg-slate-950/50 border-white/10 text-white" />
            </div>
            {utenza.servizio === 'luce' && (
              <>
                <div>
                  <Label className="text-slate-300 text-sm">POD</Label>
                  <Input value={form.pod || ''} onChange={(e) => setForm({ ...form, pod: e.target.value })} data-testid="edit-pod"
                    className="mt-1 bg-slate-950/50 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Costo €/kWh</Label>
                  <Input type="number" step="0.0001" value={form.costo_unitario || ''} onChange={(e) => setForm({ ...form, costo_unitario: e.target.value })} data-testid="edit-costo"
                    className="mt-1 bg-slate-950/50 border-white/10 text-white" />
                </div>
              </>
            )}
            {utenza.servizio === 'gas' && (
              <>
                <div>
                  <Label className="text-slate-300 text-sm">PDR</Label>
                  <Input value={form.pdr || ''} onChange={(e) => setForm({ ...form, pdr: e.target.value })} data-testid="edit-pdr"
                    className="mt-1 bg-slate-950/50 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Costo €/Smc</Label>
                  <Input type="number" step="0.0001" value={form.costo_unitario || ''} onChange={(e) => setForm({ ...form, costo_unitario: e.target.value })} data-testid="edit-costo-gas"
                    className="mt-1 bg-slate-950/50 border-white/10 text-white" />
                </div>
              </>
            )}
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Etichetta</Label>
            <Input value={form.etichetta || ''} onChange={(e) => setForm({ ...form, etichetta: e.target.value })} data-testid="edit-etichetta"
              className="mt-1 bg-slate-950/50 border-white/10 text-white" placeholder="Es. Casa Milano" />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/10 bg-transparent hover:bg-white/5 text-slate-200">
              <X className="w-4 h-4 mr-1" /> Annulla
            </Button>
            <Button type="submit" disabled={loading} data-testid="edit-submit"
              className="bg-sky-500 hover:bg-sky-400 text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-1" /> Salva</>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Alias legacy: PortalImmobile ora redirige alla dashboard
export const PortalImmobile = PortalDashboard;

// ============ Proposal Card ============
const ProposalCard = ({ proposta, onAccept, onReject, loading }) => {
  const p = proposta;
  const meta = SERV_META[p.servizio] || SERV_META.luce;
  const { Icon } = meta;
  const accepted = p.stato === 'accettata';
  const rejected = p.stato === 'rifiutata';
  const closed = accepted || rejected;
  return (
    <Card data-testid={`proposta-${p.id}`}
      className={`relative overflow-hidden border transition-all ${
        accepted ? 'bg-emerald-500/5 border-emerald-400/40'
        : rejected ? 'bg-slate-900/40 border-white/5 opacity-70'
        : 'bg-gradient-to-br from-cyan-500/10 to-sky-500/5 border-cyan-400/40 shadow-lg shadow-cyan-500/5'
      }`}
    >
      {!closed && (
        <div className="absolute -top-3 right-5 text-[10px] font-bold px-2.5 py-1 rounded-full bg-cyan-400 text-slate-950 uppercase tracking-wider z-10">
          Nuova
        </div>
      )}
      {accepted && (
        <div className="absolute -top-3 right-5 text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-500 text-white uppercase tracking-wider z-10 inline-flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Accettata
        </div>
      )}
      {rejected && (
        <div className="absolute -top-3 right-5 text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-600 text-slate-200 uppercase tracking-wider z-10">
          Rifiutata
        </div>
      )}
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          {p.compagnia_logo ? (
            <img src={p.compagnia_logo} alt={p.compagnia}
              className="w-14 h-14 rounded-xl object-contain bg-white p-1.5 shrink-0 border border-white/10" />
          ) : (
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${meta.color} grid place-items-center shrink-0`}>
              <Icon className="w-6 h-6 text-slate-950" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">
              {meta.label} · {new Date(p.created_at).toLocaleDateString('it-IT')}
            </div>
            <div className="font-bold text-base mt-0.5 truncate">{p.compagnia}</div>
            <div className="text-sm text-cyan-300 truncate">{p.offerta_nome}</div>
          </div>
        </div>

        {p.risparmio_annuo && (
          <div className="mt-4 rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Risparmio stimato</div>
            <div className="text-2xl font-extrabold text-emerald-300 mt-0.5">
              {Math.round(p.risparmio_annuo)}€<span className="text-sm text-emerald-400/80">/anno</span>
            </div>
          </div>
        )}

        {(p.prezzo_descrizione || p.durata_mesi) && (
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            {p.prezzo_descrizione && (
              <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Prezzo</div>
                <div className="font-semibold text-slate-100">{p.prezzo_descrizione}</div>
              </div>
            )}
            {p.durata_mesi && (
              <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Durata</div>
                <div className="font-semibold text-slate-100">{p.durata_mesi} mesi</div>
              </div>
            )}
          </div>
        )}

        {p.descrizione && <p className="mt-3 text-sm text-slate-300 leading-relaxed">{p.descrizione}</p>}
        {p.note_agente && (
          <div className="mt-3 text-xs text-slate-400 italic border-l-2 border-cyan-400/40 pl-3">
            Note dell'agente: {p.note_agente}
          </div>
        )}

        {!closed ? (
          <div className="mt-5 flex gap-2">
            <Button data-testid={`reject-${p.id}`} onClick={onReject} disabled={loading}
              variant="outline" className="flex-1 h-10 border-white/10 bg-transparent hover:bg-white/5 text-slate-300">
              Non mi interessa
            </Button>
            <Button data-testid={`accept-${p.id}`} onClick={onAccept} disabled={loading}
              className="flex-1 h-10 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><CheckCircle2 className="w-4 h-4 mr-1.5" /> Conferma</>)}
            </Button>
          </div>
        ) : (
          <div className="mt-5 text-xs text-center text-slate-400">
            {accepted ? 'Ti contatteremo a breve per completare l\'attivazione.' : 'Hai declinato questa offerta.'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
